import React from "react";
import { z } from "zod";
import { FormikProvider, useFormik } from "formik";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import {
    canNestGroupUnderParent,
    orderedPredefinedLeafTypes,
} from "@common/constants/domains/accounting/accountGroups";
import { AccountGroup } from "@common/types/domains/accounting/account";
import { DEPTH_LIMIT_TOOLTIP } from "@components/forms/GroupSelectField/GroupSelectField.constants";
import { validateFormikWithZodSchema } from "@helpers/form";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import {
    StandardModal,
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { AccountTypeSelectField } from "@components/forms/AccountTypeSelectField/AccountTypeSelectField";
import { GroupSelectField } from "@components/forms/GroupSelectField/GroupSelectField";
import { Field } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";
import { HStack } from "@design-system/HStack/HStack";
import { DraftGroup } from "../draftState";
import { findGroupInScope } from "../draftOperations";
import { AddFanOutTarget } from "./nestedAddScope";
import {
    NestedFooterAddSelection,
    NestedFooterCreateGroupParams,
} from "./nestedFooterAddTypes";
import styles from "./NestedFooterAddModal.module.scss";

export interface NestedAddGroupModalProps {
    isOpen: boolean;
    groups: DraftGroup[];
    // Keyed by row id, not a draft id.
    pickerGroups: AccountGroup[];
    resolveFanOutTargets: (
        selection: NestedFooterAddSelection,
    ) => AddFanOutTarget[];
    // Where the parent picker defaults: the active scope at the moment the
    // footer CTA was triggered. Null only while the modal is closed.
    defaultSelection: NestedFooterAddSelection | null;
    onCancel: () => void;
    onCreate: (params: NestedFooterCreateGroupParams) => void;
}

interface AddGroupFormValues {
    type: AccountType;
    parentRowId: string | null;
    name: string;
}

const addGroupFormSchema = z.object({
    type: z.nativeEnum(AccountType),
    parentRowId: z.string().nullable(),
    name: z.string().trim().min(1),
});

export const NestedAddGroupModal: React.FC<NestedAddGroupModalProps> = ({
    isOpen,
    groups,
    pickerGroups,
    resolveFanOutTargets,
    defaultSelection,
    onCancel,
    onCreate,
}) => {
    const formik = useFormik<AddGroupFormValues>({
        initialValues: {
            type: defaultSelection?.type ?? AccountType.operatingExpenses,
            parentRowId: defaultSelection?.parentRowId ?? null,
            name: "",
        },
        enableReinitialize: true,
        validate: validateFormikWithZodSchema(addGroupFormSchema),
        onSubmit: (values) => {
            onCreate({
                type: values.type,
                parentRowId: values.parentRowId,
                name: values.name.trim(),
            });
        },
    });

    const trimmedName = formik.values.name.trim();
    const targets = resolveFanOutTargets({
        type: formik.values.type,
        parentRowId: formik.values.parentRowId,
    });
    const isDuplicate =
        trimmedName.length > 0 &&
        targets.some(
            (target) =>
                findGroupInScope({
                    groups,
                    entityId: target.entityId,
                    type: formik.values.type,
                    parentGroupId: target.parentGroupId,
                    name: trimmedName,
                }) !== undefined,
        );
    // A preselected maximum-depth parent bypasses picker gating; disable Create too.
    const canNestUnderParent =
        targets.length > 0 &&
        targets.every((target) =>
            canNestGroupUnderParent({
                groups,
                parentGroupId: target.parentGroupId,
            }),
        );
    const canConfirm =
        trimmedName.length > 0 && !isDuplicate && canNestUnderParent;

    return (
        <StandardModal
            show={isOpen}
            onHide={onCancel}
            contentClassName={styles.modalContent}
        >
            <StandardModalHeader>Add group</StandardModalHeader>
            <StandardModalBody>
                <FormikProvider value={formik}>
                    <form onSubmit={formik.handleSubmit}>
                        <AccountTypeSelectField
                            name="type"
                            label="Type"
                            placeholder="Select a type"
                            value={formik.values.type}
                            types={orderedPredefinedLeafTypes}
                            onChange={(type) => {
                                formik.setFieldValue("type", type);
                                formik.setFieldValue("parentRowId", null);
                            }}
                        />
                        <GroupSelectField
                            parentGroupFieldName="parentRowId"
                            label="Parent group"
                            placeholder="Select a group"
                            groups={pickerGroups}
                            type={formik.values.type}
                            value={formik.values.parentRowId}
                            isGroupParentPicker
                            onChange={(parentRowId) =>
                                formik.setFieldValue("parentRowId", parentRowId)
                            }
                        />
                        {!canNestUnderParent && (
                            <span className={styles.depthError}>
                                {DEPTH_LIMIT_TOOLTIP}
                            </span>
                        )}

                        <Field
                            className={styles.nameField}
                            label="Name"
                            invalid={isDuplicate}
                            error={
                                isDuplicate
                                    ? "A group with this name already exists"
                                    : undefined
                            }
                        >
                            <Input
                                autoFocus
                                type="text"
                                placeholder="e.g. Marketing"
                                size="xl"
                                value={formik.values.name}
                                invalid={isDuplicate}
                                onChange={(event) => {
                                    void formik.setFieldValue(
                                        "name",
                                        event.target.value,
                                    );
                                }}
                            />
                        </Field>

                        <HStack
                            as="footer"
                            gap={12}
                            justify="end"
                            className="mt-4"
                        >
                            <Button
                                variant="secondary"
                                size="xl"
                                type="button"
                                onClick={onCancel}
                            >
                                Cancel
                            </Button>
                            <ButtonWithLoader
                                variant="primary"
                                size="xl"
                                loading={false}
                                type="submit"
                                disabled={!canConfirm}
                                hotKey="mod+enter"
                                hotKeyOptions={{
                                    description: "Add group",
                                    preventDefault: true,
                                    enableOnFormTags: true,
                                }}
                            >
                                Create
                            </ButtonWithLoader>
                        </HStack>
                    </form>
                </FormikProvider>
            </StandardModalBody>
        </StandardModal>
    );
};
